/*
 * gmx-discounts.js — shows the store's automatic "Glitterati" volume discounts
 * on the storefront, driven by what is actually in the cart.
 *
 * Why this file exists. Shopify applies the discounts itself, cart-wide: two
 * or more design units take 20% off every design line, four or more take 35%.
 * The shopper only saw that in the cart, so every page before the cart quoted
 * a price the cart then beat. Liquid cannot fix this — storefront pages are
 * full-page cached, so the server has no idea what is in the shopper's cart.
 * Everything cart-dependent therefore happens here, client side.
 *
 * Sources of truth, both outside this file:
 *   - the tiers and the entitled product types come from the JSON block that
 *     snippets/gmx-discount-config.liquid renders in <head>;
 *   - the counts and the money come from /cart.js, never from a guess.
 *
 * What it paints:
 *   1. Product cards — the card price becomes "discounted <s>original</s>"
 *      plus a small chip, for entitled products only, at the tier the CART has
 *      already reached. An empty cart leaves every card exactly as the server
 *      rendered it.
 *   2. The product page — the same, but the count also includes the quantity
 *      sitting in the stepper, so the page answers "what will I pay if I add
 *      this?". It also writes the tier band's status line.
 *   3. The free-glitter deal (1 free glitter per design, paid at checkout):
 *      while the cart's designs outnumber its glitters, every Glitter card
 *      shows "Free <s>original</s>" plus a chip with how many are left, and a
 *      Glitter product page shows "Free" while the allowance covers the
 *      stepper quantity. Once the allowance is spent, normal prices return.
 *
 * What it publishes:
 *   document event `gmx:discount-state`, detail
 *     { count, tier, nextTier, pdpCount, pdpTier, freeGlitterLeft }
 *   and the same object on window.gmxDiscounts.state, so a listener that
 *   starts late can read the current state instead of waiting for the next
 *   event. sections/gmx-product.liquid's tier band lights up from this event
 *   (assets/gmx-product.js owns that animation).
 *
 * What it listens to:
 *   DOMContentLoaded, `pageshow` (a bfcache restore can show a stale cart),
 *   Dawn's pubsub `cart-update`, and the theme-owned `gmx:cart-changed`, which
 *   gmx-product.js, gmx-quick-add.js and gmx-cart.js dispatch after every
 *   successful cart write.
 */
(function () {
  "use strict";

  /* ---------------------------------------------------------------- helpers */

  /* Whole amounts print as "$18", not "$18.00" — same rule as the Liquid side,
     which uses the money_without_trailing_zeros filter. */
  function money(cents) {
    var code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";
    var whole = cents % 100 === 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
        style: "currency",
        currency: code,
        minimumFractionDigits: whole ? 0 : 2,
      }).format(cents / 100);
    } catch (e) {
      return "$" + (whole ? String(cents / 100) : (cents / 100).toFixed(2));
    }
  }

  /* Rounded to the nearest cent, which is what Shopify does: a $18.00 line at
     35% off comes back from /cart.js as 1170, not 1170.0000001. */
  function discountedCents(cents, pct) {
    return Math.round((cents * (100 - pct)) / 100);
  }

  /* The highest tier the count has reached, or null. `tiers` is ascending. */
  function tierFor(count, tiers) {
    var hit = null;
    for (var i = 0; i < tiers.length; i++) {
      if (count >= tiers[i].min) hit = tiers[i];
    }
    return hit;
  }

  /* The cheapest tier still ahead of the count, or null when none is left. */
  function nextTierFor(count, tiers) {
    for (var i = 0; i < tiers.length; i++) {
      if (count < tiers[i].min) return tiers[i];
    }
    return null;
  }

  function plural(n, word) {
    return n + " " + word + (n === 1 ? "" : "s");
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  /* --------------------------------------------------------------- the state */

  /* Published before anything else can fail, so the helpers stay reachable
     (assets/gmx-product.js reads .state; the node unit test reads the rest). */
  var api = {
    state: { count: 0, tier: null, nextTier: null, pdpCount: 0, pdpTier: null, freeGlitterLeft: 0 },
    money: money,
    discountedCents: discountedCents,
    tierFor: tierFor,
    nextTierFor: nextTierFor,
  };
  window.gmxDiscounts = api;

  function readConfig() {
    var node = document.querySelector("[data-gmx-discount-config]");
    if (!node) return null;
    try {
      var parsed = JSON.parse(node.textContent);
      parsed.tiers = (parsed.tiers || []).slice().sort(function (a, b) {
        return a.min - b.min;
      });
      parsed.entitledTypes = (parsed.entitledTypes || []).map(function (t) {
        return String(t).toLowerCase().trim();
      });
      return parsed;
    } catch (e) {
      return null;
    }
  }

  var CONFIG = readConfig();
  if (!CONFIG) return;
  /* The two deals switch independently: the volume tiers come from theme
     settings, the free-glitter deal ships whenever its config block does. */
  var TIERS_ON = !!(CONFIG.enabled && CONFIG.tiers.length);
  var FREE_ON = !!(CONFIG.freeGlitter && CONFIG.freeGlitter.perTypes && CONFIG.freeGlitter.perTypes.length);
  if (!TIERS_ON && !FREE_ON) return;

  function lineType(line) {
    return String(line.product_type || "")
      .toLowerCase()
      .trim();
  }

  function entitledUnits(cart) {
    var total = 0;
    (cart.items || []).forEach(function (line) {
      if (CONFIG.entitledTypes.indexOf(lineType(line)) !== -1) total += line.quantity || 0;
    });
    return total;
  }

  /* Free glitters still unclaimed: 1 per design in the cart, minus the glitter
     units already there. Never negative. */
  function freeGlitterLeft(cart) {
    if (!FREE_ON) return 0;
    var perTypes = CONFIG.freeGlitter.perTypes.map(function (t) {
      return String(t).toLowerCase().trim();
    });
    var glitterType = String(CONFIG.freeGlitter.glitterType || "glitter")
      .toLowerCase()
      .trim();
    var designs = 0;
    var glitters = 0;
    (cart.items || []).forEach(function (line) {
      var type = lineType(line);
      if (perTypes.indexOf(type) !== -1) designs += line.quantity || 0;
      else if (type === glitterType) glitters += line.quantity || 0;
    });
    return Math.max(0, designs - glitters);
  }

  /* ----------------------------------------------------------- product cards */

  /* The server-rendered markup is kept verbatim in a data attribute, so the
     "no tier" case is a restore, not a re-render — including the translated
     "From" string and anything else Liquid put in there. */
  function paintCards(tier) {
    var cards = document.querySelectorAll("[data-gmx-card-price][data-gmx-entitled='true']");
    Array.prototype.forEach.call(cards, function (el) {
      if (el.getAttribute("data-gmx-card-original") === null) {
        el.setAttribute("data-gmx-card-original", el.innerHTML);
      }
      if (!tier) {
        el.innerHTML = el.getAttribute("data-gmx-card-original");
        el.classList.remove("gmx-card__price--discounted");
        return;
      }
      var cents = parseInt(el.getAttribute("data-gmx-card-cents"), 10);
      if (!isFinite(cents) || cents <= 0) return;
      var from = el.getAttribute("data-gmx-card-from") === "true";
      el.classList.add("gmx-card__price--discounted");
      el.innerHTML =
        (from ? "From " : "") +
        '<span class="gmx-card__price-now"></span>' +
        '<span class="gmx-card__price-sub"><s class="gmx-card__price-was"></s>' +
        '<span class="gmx-card__price-chip"></span></span>';
      el.querySelector(".gmx-card__price-now").textContent = money(discountedCents(cents, tier.pct));
      el.querySelector(".gmx-card__price-was").textContent = money(cents);
      el.querySelector(".gmx-card__price-chip").textContent = "-" + tier.pct + "%";
    });
  }

  /* Same restore-or-rewrite contract as paintCards, for Glitter cards: while
     the shopper still has free glitters coming, the price reads "Free" and the
     chip counts what is left. */
  function paintGlitterCards(left) {
    var cards = document.querySelectorAll("[data-gmx-card-price][data-gmx-glitter='true']");
    Array.prototype.forEach.call(cards, function (el) {
      if (el.getAttribute("data-gmx-card-original") === null) {
        el.setAttribute("data-gmx-card-original", el.innerHTML);
      }
      if (!left) {
        el.innerHTML = el.getAttribute("data-gmx-card-original");
        el.classList.remove("gmx-card__price--discounted", "gmx-card__price--free");
        return;
      }
      var cents = parseInt(el.getAttribute("data-gmx-card-cents"), 10);
      el.classList.add("gmx-card__price--discounted", "gmx-card__price--free");
      el.innerHTML =
        '<span class="gmx-card__price-now">Free</span>' +
        '<span class="gmx-card__price-sub"><s class="gmx-card__price-was"></s>' +
        '<span class="gmx-card__price-chip"></span></span>';
      el.querySelector(".gmx-card__price-was").textContent = isFinite(cents) && cents > 0 ? money(cents) : "";
      el.querySelector(".gmx-card__price-chip").textContent = left === 1 ? "1 left" : left + " left";
    });
  }

  /* ------------------------------------------------------------ product page */

  var pdp = null;

  function findPdp() {
    var row = document.querySelector(".gmx-pdp [data-gmx-price-row]");
    if (!row) return null;
    return {
      root: document.querySelector(".gmx-pdp"),
      row: row,
      entitled: row.getAttribute("data-gmx-entitled") === "true",
      glitter: row.getAttribute("data-gmx-glitter") === "true",
      price: row.querySelector("[data-gmx-price]"),
      compare: row.querySelector("[data-gmx-compare]"),
      auto: document.querySelector(".gmx-pdp [data-gmx-discount-auto]"),
      status: document.querySelector(".gmx-pdp [data-gmx-tier-status]"),
      qty: document.querySelector(".gmx-pdp [data-gmx-qty]"),
      addBtn: document.querySelector(".gmx-pdp [data-gmx-add]"),
    };
  }

  /* The stepper is owned by gmx-product.js, which keeps the add button's
     data-unit-price current through variant switches. Read it there, so the
     two files cannot disagree about which variant is selected. */
  function unitCents() {
    var live = pdp.addBtn ? parseInt(pdp.addBtn.getAttribute("data-unit-price"), 10) : NaN;
    if (isFinite(live) && live > 0) return live;
    return parseInt(pdp.row.getAttribute("data-gmx-unit-cents"), 10) || 0;
  }

  function pdpQuantity() {
    var n = pdp.qty ? parseInt(pdp.qty.value, 10) : 1;
    return isFinite(n) && n > 0 ? n : 1;
  }

  /* The compare-at strike this product would show with no discount running.
     Taken from the selected variant button when there is one, because that is
     where gmx-product.js keeps each variant's compare-at. */
  function baseCompare() {
    var active = pdp.root.querySelector("[data-gmx-variant].is-active");
    if (active) return active.getAttribute("data-compare") || "";
    return pdp.row.getAttribute("data-gmx-compare-original") || "";
  }

  function pill() {
    var el = pdp.row.querySelector("[data-gmx-discount-pill]");
    if (!el) {
      el = document.createElement("span");
      el.className = "gmx-pdp__discount-pill";
      el.setAttribute("data-gmx-discount-pill", "");
      pdp.row.appendChild(el);
    }
    return el;
  }

  function paintPdp(tier, count) {
    if (!pdp || !pdp.entitled) return;
    var unit = unitCents();

    if (!tier) {
      if (pdp.price) pdp.price.textContent = money(unit);
      if (pdp.compare) {
        var original = baseCompare();
        pdp.compare.textContent = original;
        pdp.compare.hidden = !original;
      }
      pill().hidden = true;
      if (pdp.auto) pdp.auto.hidden = true;
      return;
    }

    if (pdp.price) pdp.price.textContent = money(discountedCents(unit, tier.pct));
    /* One strike only. When a tier is live the struck figure is the price the
       shopper would otherwise pay, so it replaces the compare-at rather than
       stacking a second strike next to it. */
    if (pdp.compare) {
      pdp.compare.textContent = money(unit);
      pdp.compare.hidden = false;
    }
    var p = pill();
    p.textContent = tier.pct + "% OFF · " + tier.name;
    p.hidden = false;
    if (pdp.auto) pdp.auto.hidden = false;
  }

  /* A Glitter product page: "Free" while the allowance covers the stepper
     quantity, an honest split when it only covers part of it, and the plain
     restore when it is spent. Never runs for tier-entitled products, which
     paintPdp owns. */
  function paintPdpGlitter(left) {
    if (!pdp || !pdp.glitter) return;
    var unit = unitCents();
    var qty = pdpQuantity();
    var p = pill();

    if (left >= qty) {
      if (pdp.price) pdp.price.textContent = "Free";
      if (pdp.compare) {
        pdp.compare.textContent = money(unit);
        pdp.compare.hidden = false;
      }
      p.textContent = left === 1 ? "1 free glitter left" : left + " free glitters left";
      p.hidden = false;
    } else if (left > 0) {
      if (pdp.price) pdp.price.textContent = money(unit);
      if (pdp.compare) {
        var original = baseCompare();
        pdp.compare.textContent = original;
        pdp.compare.hidden = !original;
      }
      p.textContent = left + " of " + qty + " free at checkout";
      p.hidden = false;
    } else {
      if (pdp.price) pdp.price.textContent = money(unit);
      if (pdp.compare) {
        var base = baseCompare();
        pdp.compare.textContent = base;
        pdp.compare.hidden = !base;
      }
      p.hidden = true;
    }
  }

  function paintStatus(count, tier, next) {
    if (!pdp || !pdp.status) return;
    var text;
    if (tier && next) {
      text =
        "You have " +
        plural(count, "design") +
        " — " +
        tier.pct +
        "% off is active. Add " +
        (next.min - count) +
        " more for " +
        next.pct +
        "% off.";
    } else if (tier) {
      text = "You have " + plural(count, "design") + " — " + tier.pct + "% off is active.";
    } else if (next) {
      text = "Add " + plural(next.min - count, "design") + " to unlock " + next.pct + "% off.";
    } else {
      text = "";
    }
    pdp.status.textContent = text;
    pdp.status.hidden = !text;
  }

  /* ------------------------------------------------------------------ paint */

  function paint(cartCount) {
    var tier = TIERS_ON ? tierFor(cartCount, CONFIG.tiers) : null;
    var next = TIERS_ON ? nextTierFor(cartCount, CONFIG.tiers) : null;

    /* Cards show the tier the shopper is already at. The product page adds the
       quantity in the stepper, because that is the question that page asks. */
    var pdpCount = cartCount;
    if (pdp && pdp.entitled) pdpCount = cartCount + pdpQuantity();
    var pdpTier = TIERS_ON ? tierFor(pdpCount, CONFIG.tiers) : null;
    var pdpNext = TIERS_ON ? nextTierFor(pdpCount, CONFIG.tiers) : null;

    if (TIERS_ON) {
      paintCards(tier);
      paintPdp(pdpTier, pdpCount);
      paintStatus(pdpCount, pdpTier, pdpNext);
    }
    if (FREE_ON) {
      paintGlitterCards(lastFreeLeft);
      paintPdpGlitter(lastFreeLeft);
    }

    api.state = {
      count: cartCount,
      tier: tier,
      nextTier: next,
      pdpCount: pdpCount,
      pdpTier: pdpTier,
      freeGlitterLeft: lastFreeLeft,
    };
    document.dispatchEvent(new CustomEvent("gmx:discount-state", { detail: api.state }));
  }

  /* ------------------------------------------------------------------- init */

  var lastCartCount = 0;
  var lastFreeLeft = 0;

  function refresh() {
    return fetch(root() + "cart.js", { headers: { Accept: "application/json" } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        lastCartCount = entitledUnits(cart);
        lastFreeLeft = freeGlitterLeft(cart);
        paint(lastCartCount);
      })
      .catch(function () {
        /* A failed cart read leaves the server-rendered prices standing, which
           is the safe direction: never quote a discount we cannot confirm. */
      });
  }

  function init() {
    pdp = findPdp();

    /* The stepper's ± buttons set input.value in gmx-product.js's own click
       handler, and a programmatic value change fires no input event. Reading
       on the next tick is how initTiers already does it. */
    if (pdp && pdp.qty) {
      var onQty = function () {
        setTimeout(function () {
          paint(lastCartCount);
        }, 0);
      };
      pdp.qty.addEventListener("input", onQty);
      pdp.qty.addEventListener("change", onQty);
      Array.prototype.forEach.call(pdp.root.querySelectorAll("[data-gmx-qty-step]"), function (btn) {
        btn.addEventListener("click", onQty);
      });
      /* A variant switch changes the unit price. gmx-product.js writes it to
         the add button, so watching that attribute needs no second listener
         and no event contract between the two files. */
      if (pdp.addBtn && window.MutationObserver) {
        new MutationObserver(function () {
          paint(lastCartCount);
        }).observe(pdp.addBtn, { attributes: true, attributeFilter: ["data-unit-price"] });
      }
    }

    /* Dawn's own cart code publishes this; the gmx-* cart code does not, which
       is why the theme-owned event above exists too. Both are harmless
       together — a double refresh just paints the same numbers. Subscribed
       here, not at load time: pubsub.js is a later deferred script, so
       `subscribe` does not exist yet when this file first runs. */
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
    }

    refresh();
  }

  document.addEventListener("gmx:cart-changed", refresh);
  /* A bfcache restore replays a page whose cart may have moved on since. */
  window.addEventListener("pageshow", function (event) {
    if (event.persisted) refresh();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
